import { createContext, type ReactNode, useContext, useLayoutEffect } from 'react';
import { type ModernSubscription, type PlanLimitsType, PlanTypes, SubscriptionStates } from '@gonfalon/billing/types';

import { type Account, PlanType, SubscriptionState } from '../model';

type SubscriptionData = {
  _limits?: Partial<PlanLimitsType>;
  planType?: PlanType; // Plan type from the subscription endpoint
};

// Transform vitalstatistix types to gonfalon billing types
const transformSubscriptionState = (vitalState?: SubscriptionState): string => {
  if (!vitalState) {
    return SubscriptionStates.UNKNOWN;
  }

  switch (vitalState) {
    case SubscriptionState.ACTIVE_SUBSCRIPTION:
      return SubscriptionStates.ACTIVE_SUBSCRIPTION;
    case SubscriptionState.ACTIVE_TRIAL:
      return SubscriptionStates.ACTIVE_TRIAL;
    case SubscriptionState.ACTIVE_TRIAL_WITH_SUBSCRIPTION:
      return SubscriptionStates.ACTIVE_TRIAL_WITH_SUBSCRIPTION;
    case SubscriptionState.GRACE_PERIOD_TRIAL:
      return SubscriptionStates.GRACE_PERIOD_TRIAL;
    case SubscriptionState.LAPSED_TRIAL:
      return SubscriptionStates.LAPSED_TRIAL;
    case SubscriptionState.FAILED_PAYMENT_SUBSCRIPTION:
      return SubscriptionStates.FAILED_PAYMENT_SUBSCRIPTION;
    case SubscriptionState.PENDING_CANCELED_SUBSCRIPTION:
      return SubscriptionStates.PENDING_CANCELED_SUBSCRIPTION;
    case SubscriptionState.CANCELED_SUBSCRIPTION:
      return SubscriptionStates.CANCELED_SUBSCRIPTION;
    case SubscriptionState.LAPSED_SUBSCRIPTION:
      return SubscriptionStates.LAPSED_SUBSCRIPTION;
    case SubscriptionState.USAGE_EXCEEDED_GRACE_PERIOD:
      return SubscriptionStates.USAGE_EXCEEDED_GRACE_PERIOD;
    case SubscriptionState.USAGE_EXCEEDED_READ_ONLY:
      return SubscriptionStates.USAGE_EXCEEDED_READ_ONLY;
    default:
      return SubscriptionStates.UNKNOWN;
  }
};

const transformPlanType = (vitalPlan?: PlanType): PlanTypes => {
  if (!vitalPlan) {
    return PlanTypes.LEGACY;
  }

  switch (vitalPlan) {
    case PlanType.STARTER:
      return PlanTypes.STARTER;
    case PlanType.STARTER_2021:
      return PlanTypes.STARTER2021;
    case PlanType.STANDARD_TRIAL:
      return PlanTypes.STANDARD_TRIAL;
    case PlanType.STANDARD_TRIAL_2021:
      return PlanTypes.STANDARD_TRIAL2021;
    case PlanType.PROFESSIONAL:
      return PlanTypes.PROFESSIONAL;
    case PlanType.PROFESSIONAL_2021:
      return PlanTypes.PROFESSIONAL2021;
    case PlanType.ENTERPRISE_TRIAL:
      return PlanTypes.ENTERPRISE_TRIAL;
    case PlanType.ENTERPRISE:
      return PlanTypes.ENTERPRISE;
    case PlanType.ENTERPRISE_2023:
      return PlanTypes.ENTERPRISE2023;
    case PlanType.FOUNDATION_2023:
      return PlanTypes.FOUNDATION2023;
    case PlanType.FOUNDATION_TRIAL_2023:
      return PlanTypes.FOUNDATION_TRIAL2023;
    case PlanType.GUARDIAN_2024:
      return PlanTypes.GUARDIAN2024;
    case PlanType.GUARDIAN_SEATS_2024:
      return PlanTypes.GUARDIAN_SEATS2024;
    case PlanType.GUARDIAN_TRIAL_2024:
      return PlanTypes.GUARDIAN_TRIAL2024;
    case PlanType.DEVELOPER_2025:
      return PlanTypes.DEVELOPER2025;
    case PlanType.FOUNDATION_2025:
      return PlanTypes.FOUNDATION2025;
    case PlanType.LEGACY:
    default:
      return PlanTypes.LEGACY;
  }
};

// Transform vitalstatistix account to gonfalon account context format
const transformAccountToGonfalonContext = (account: Account, subscription?: SubscriptionData) => {
  const modernSubscription: ModernSubscription = {
    state: transformSubscriptionState(account.subscription?.state),
    planType: transformPlanType(subscription?.planType ?? PlanType.LEGACY),
    _limits: subscription?._limits,
    _links: account._links,
  };

  return {
    profile: {
      // Mock profile data - in a real implementation this would come from the authenticated user
      email: account.ownerEmail,
      firstName: '',
      lastName: '',
    },
    account: {
      id: account._id,
      name: account.organization || account._id,
      _links: account._links,
    },
    subscription: modernSubscription,
    isLegacySubscription: false,
  } as const;
};

// Define the context type to match gonfalon's useAccountContext return type
type AccountContextType = ReturnType<typeof transformAccountToGonfalonContext>;

const AccountContext = createContext<AccountContextType | null>(null);

function publishCurrentAccount(account: Account) {
  if (typeof window === 'undefined') {
    return;
  }
  window.__VITAL_CURRENT_ACCOUNT__ = account;
  if (typeof window.__GONFALON_RESET_CLIENT__ === 'function') {
    window.__GONFALON_RESET_CLIENT__();
  }
}

// Renders ahead of the provider's children, so `window.__VITAL_CURRENT_ACCOUNT__`
// is current before those children render. It publishes on every render whose
// account differs from the global, so a render that React discards (an
// interrupted transition to another account) can't leave the global pointing
// at that account. The layout effect repeats the write when the account object
// changes but the id does not.
function PublishCurrentAccount({ account }: { account: Account }) {
  if (window.__VITAL_CURRENT_ACCOUNT__?._id !== account._id) {
    publishCurrentAccount(account);
  }
  useLayoutEffect(() => {
    publishCurrentAccount(account);
  }, [account]);
  return null;
}

type AccountContextProviderProps = {
  account: Account;
  subscription?: SubscriptionData; // Optional subscription data with limits from vitalstatistix
  children: ReactNode;
};

/**
 * AccountContextProvider bridges vitalstatistix account data with gonfalon billing components
 * by making the account data available globally for gonfalon's useAccountContext hook.
 */
export function AccountContextProvider({ account, subscription, children }: AccountContextProviderProps) {
  const contextValue = transformAccountToGonfalonContext(account, subscription);

  return (
    <AccountContext.Provider value={contextValue}>
      <PublishCurrentAccount key={account._id} account={account} />
      {children}
    </AccountContext.Provider>
  );
}

/**
 * Hook that provides account context data in the format expected by gonfalon billing components.
 * This mimics the interface of @gonfalon/app-context's useAccountContext hook.
 */
export function useAccountContext(): AccountContextType {
  const context = useContext(AccountContext);

  if (!context) {
    throw new Error('useAccountContext must be used within an AccountContextProvider');
  }

  return context;
}
